import React, { useCallback, useMemo } from 'react'

import { useReactFlow } from '@xyflow/react'

import Box from 'components_new/atoms/Box'

import Node, { InputType } from 'components_new/molecules/Node'
import FieldSelect, {
  FieldOption
} from 'components_new/molecules/Node/FieldSelect'

import NodeType from 'types/transformation/enums/NodeType'
import {
  LowerNode,
  Node as TNode
} from 'types/transformation/types/nodes'

import { pruneRef, useInputSchema, useIsInvalid, usePruneRefs, useColumnOrigins } from './hooks'

interface LowerNodeProps {
  id: string
  data: LowerNode
  selected: boolean
}

const Lower = (props: LowerNodeProps) => {
  const { id, data, selected } = props

  const { setNodes } = useReactFlow<TNode>()

  const isInvalid = useIsInvalid(data)
  const inputSchema = useInputSchema(data.left_node)

  const originByNode = useColumnOrigins()

  const fieldOptions = useMemo(
    (): FieldOption[] =>
      inputSchema.map((column) => ({
        id: column.id,
        name: column.name,
        type: column.type,
        nodeIndex: column.reference_node_index,
        origin: originByNode.get(column.reference_node) ?? null
      })),
    [inputSchema, originByNode]
  )

  // input schema has changed, validate references
  usePruneRefs<LowerNode>(
    id,
    (nodeData) => ({
      ...nodeData,
      left_column: pruneRef(nodeData.left_column, inputSchema)
    }),
    [inputSchema]
  )

  const handleSelected = useCallback(
    (leftColumn: string) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, left_column: leftColumn } }
            : node
        )
      )
    },
    [setNodes, id]
  )

  return (
    <Node
      error={isInvalid}
      icon="TransformOutlined"
      inputType={InputType.SINGLE}
      title={'Lower'}
      selected={selected}
      nodeNumber={data.index}
      type={NodeType.LOWER}
    >
      <Box sx={{ width: '272px' }}>
        <FieldSelect
          value={data.left_column}
          options={fieldOptions}
          onChange={handleSelected}
          placeholder="Välj attribut"
        />
      </Box>
    </Node>
  )
}

export default Lower
